<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import EmailCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/EmailCampaign/EmailCampaignDialog.vue';

const store = useStore();
const router = useRouter();

const showCreateDialog = ref(false);
const deletingBatchId = ref(null);
const confirmDeleteRef = ref(null);

const uiFlags = useMapGetter('campaignBatches/getUIFlags');
const allBatches = useMapGetter('campaignBatches/getBatches');

const batches = computed(() =>
  (allBatches.value || []).filter(b => b.batch_type === 'email_marketing')
);
const isFetching = computed(() => uiFlags.value.isFetching);
const hasNoBatches = computed(() => batches.value?.length === 0 && !isFetching.value);

onMounted(() => {
  store.dispatch('campaignBatches/get');
});

const statusConfig = {
  draft: { class: 'text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-700/40', icon: 'i-lucide-file-edit', label: 'Rascunho' },
  ready: { class: 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30', icon: 'i-lucide-check-circle', label: 'Pronto' },
  sending: { class: 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30', icon: 'i-lucide-loader animate-spin', label: 'Enviando' },
  completed: { class: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30', icon: 'i-lucide-check-check', label: 'Concluído' },
  failed: { class: 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30', icon: 'i-lucide-alert-circle', label: 'Falhou' },
};

const getStatusConfig = status => statusConfig[status] || statusConfig.draft;

const navigateToBatch = batch => {
  router.push({ name: 'campaigns_batch_detail', params: { batchId: batch.id } });
};

const handleCreateSuccess = () => {
  showCreateDialog.value = false;
  store.dispatch('campaignBatches/get');
};

const handleDelete = batch => {
  deletingBatchId.value = batch.id;
  confirmDeleteRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await store.dispatch('campaignBatches/delete', deletingBatchId.value);
    useAlert('Campanha excluída.');
  } catch {
    useAlert('Erro ao excluir campanha.');
  } finally {
    deletingBatchId.value = null;
  }
};

const formatDate = d => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-surface-1 overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0">
      <div>
        <h1 class="text-lg font-semibold text-n-slate-12">📧 Campanhas de E-mail</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">Dispare campanhas de e-mail via Amazon SES por segmentação de contatos.</p>
      </div>
      <Button
        icon="i-lucide-plus"
        label="Nova Campanha"
        @click="showCreateDialog = true"
      />
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-auto p-6">
        <!-- Loading -->
        <div v-if="isFetching" class="flex justify-center py-16">
          <Spinner />
        </div>

        <!-- Empty -->
        <div v-else-if="hasNoBatches" class="flex flex-col items-center py-20 gap-4">
          <div class="size-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
            <span class="i-lucide-mail text-blue-500 size-8" />
          </div>
          <div class="text-center">
            <h3 class="text-sm font-semibold text-n-slate-12">Nenhuma campanha de e-mail</h3>
            <p class="text-xs text-n-slate-10 mt-1">Crie sua primeira campanha e envie via Amazon SES.</p>
          </div>
          <Button icon="i-lucide-plus" label="Nova Campanha" @click="showCreateDialog = true" />
        </div>

        <!-- List -->
        <div v-else class="max-w-4xl mx-auto flex flex-col gap-3">
          <div
            v-for="batch in batches"
            :key="batch.id"
            class="rounded-xl bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
            @click="navigateToBatch(batch)"
          >
            <!-- Icon -->
            <div class="size-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <span class="i-lucide-mail text-blue-500 size-5" />
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-0.5">
                <span class="text-sm font-semibold text-n-slate-12 truncate">{{ batch.name }}</span>
                <span
                  class="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  :class="getStatusConfig(batch.status).class"
                >
                  {{ getStatusConfig(batch.status).label }}
                </span>
              </div>
              <p v-if="batch.subject" class="text-xs text-n-slate-10 truncate">Assunto: {{ batch.subject }}</p>
              <div class="flex items-center gap-4 mt-1">
                <span class="text-[11px] text-n-slate-9">
                  <span class="i-lucide-users size-3 inline-block mr-0.5" />
                  {{ batch.contacts_count || 0 }} destinatários
                </span>
                <span v-if="batch.scheduled_at" class="text-[11px] text-n-slate-9">
                  <span class="i-lucide-clock size-3 inline-block mr-0.5" />
                  {{ formatDate(batch.scheduled_at) }}
                </span>
                <span v-if="batch.sent_count" class="text-[11px] text-emerald-600">
                  <span class="i-lucide-check size-3 inline-block mr-0.5" />
                  {{ batch.sent_count }} enviados
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-2 flex-shrink-0" @click.stop>
              <button
                class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Excluir"
                @click="handleDelete(batch)"
              >
                <span class="i-lucide-trash-2 size-4 text-red-400" />
              </button>
            </div>
          </div>
        </div>
      </div>
    <!-- Create Dialog -->
    <EmailCampaignDialog
      v-if="showCreateDialog"
      @close="showCreateDialog = false"
      @success="handleCreateSuccess"
    />

    <!-- Confirm Delete -->
    <Dialog ref="confirmDeleteRef" title="Excluir campanha?" @confirm="confirmDelete">
      <p class="text-sm text-n-slate-11">Esta ação não pode ser desfeita.</p>
    </Dialog>
  </div>
</template>
